import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { scrollToSection } from "@/lib/api";

const STEPS = [
  {
    title: "Profile evaluation & university selection",
    desc: "We study your academics, budget and goals, then shortlist countries and universities where your odds are genuinely strong.",
  },
  {
    title: "Applications & SOP refinement",
    desc: "Documentation, applications and statement-of-purpose edits that make admissions teams sit up — with deadlines tracked to the day.",
  },
  {
    title: "Visa guidance & mock interviews",
    desc: "Country-specific visa files, financial-proof planning and embassy-style mock interviews until you walk in calm.",
  },
  {
    title: "Loan sanction & pre-departure support",
    desc: "Education loans, accommodation, forex, insurance and airport pickup — everything settled before your flight takes off.",
  },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="border-y border-white/10 bg-surface/40">
      <div className="container-x grid grid-cols-1 gap-14 py-20 lg:grid-cols-12 lg:gap-10 lg:py-32">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <SectionHeading
              overline="How it works"
              title={
                <>
                  Four steps between you and{" "}
                  <span className="italic text-mint">campus abroad.</span>
                </>
              }
              desc="A journey refined by 1,500+ student files. No guesswork, no last-minute panic — a route mapped from day one."
            />
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => scrollToSection("#book")}
              data-testid="process-cta-btn"
              className="group mt-9 flex items-center gap-2 rounded-full border border-mint/40 px-6 py-3 text-sm font-semibold text-mint transition-all duration-300 hover:bg-mint hover:text-ink"
            >
              Begin with step one
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        </div>

        <div className="lg:col-span-7">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.05 * i }}
              data-testid={`process-step-${i + 1}`}
              className={`flex gap-6 py-9 lg:gap-10 lg:py-12 ${i > 0 ? "border-t border-white/10" : ""} ${i === 0 ? "lg:pt-2" : ""}`}
            >
              <span className="font-mono text-sm text-mint/80 lg:text-base">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-2xl font-normal leading-snug tracking-tight sm:text-3xl">
                  {s.title}
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-mist sm:text-[15px]">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
